import React, { ReactNode, ReactElement } from 'react';
import { List, Box, SxProps, Theme, ListSubheader, Divider } from '@mui/material';
import { spaces, typography } from '../../../assets/styles/themes/tokens';
import MenuItem from './MenuItem';
import { IconName } from '../../../utils/getMuiIcon';

export interface MenuItemConfig {
  icon: IconName;
  label: string;
  to?: string;
  external?: boolean;
  onClick?: () => void;
  activePaths?: string[];
  isFocused?: boolean;
  renderWrapper?: (children: ReactElement<any, any> & ReactNode) => React.ReactNode;
}

interface CustomSubListProps {
  items: MenuItemConfig[];
  isCollapsed: boolean;
  sectionTitle?: string;
  showSectionHeader?: boolean;
  highlighted?: boolean;
  /** When true, no divider/subheader is shown when sidebar is collapsed */
  hideSubheaderWhenCollapsed?: boolean;
}

const getSubListStyles = {
  padding: 0,
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: spaces.xxs,
};

const highlightedContainerStyles: SxProps<Theme> = {
  backgroundColor: 'action.hover',
  borderRadius: 1,
  margin: spaces.xs,
  padding: spaces.xs,
  border: '1px solid',
  borderColor: 'divider',
};

const dividerStyles: SxProps<Theme> = { 
  display: 'flex',
  height: '24px',
  padding: `0 ${spaces.xs}`,
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  alignSelf: 'stretch',
};

export const CustomSubList: React.FC<CustomSubListProps> = ({
  items,
  isCollapsed,
  sectionTitle,
  showSectionHeader = true,
  highlighted = false,
  hideSubheaderWhenCollapsed = false,
}) => {
  const renderSubheader = () => {
    if (!showSectionHeader || !sectionTitle) return null;
    if (isCollapsed && hideSubheaderWhenCollapsed) return null;
    
    if (isCollapsed) {
      return (
        <ListSubheader
          disableSticky
          sx={dividerStyles}
        >
          <Divider sx={{ width: '100%' }} />
        </ListSubheader>
      );
    }

    return (
      <ListSubheader
        disableSticky
        sx={{
          paddingX: spaces.sm,
          paddingY: 0,
          ...typography.menu.sectionHeader,
        }}
      >
        {sectionTitle}
      </ListSubheader>
    );
  };

  const content = (
    <List 
      dense 
      disablePadding 
      sx={getSubListStyles}
      subheader={renderSubheader()}
    >
      {items.map((item) => {
        const key = `${item.label}-${item.to || item.icon}`;
        const menuItem = (
          <MenuItem
            icon={item.icon}
            label={item.label}
            to={item.to}
            external={item.external}
            onClick={item.onClick}
            activePaths={item.activePaths}
            isCollapsed={isCollapsed}
            isFocused={item.isFocused}
          />
        );

        return item.renderWrapper ? (
          <React.Fragment key={key}>
            {item.renderWrapper(menuItem)}
          </React.Fragment>
        ) : (
          <React.Fragment key={key}>{menuItem}</React.Fragment>
        );
      })}
    </List>
  );

  if (highlighted) {
    return <Box sx={highlightedContainerStyles}>{content}</Box>;
  }

  return content;
};

export default CustomSubList;

